<template>
    <div class="module-container">
        <h3>数据上传</h3>

        <!-- 消息提示 -->
        <div v-if="message" class="success-message">{{ message }}</div>
        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

        <!-- 操作按钮 -->
        <div class="action-buttons">
            <button @click="loadCurrentData" class="load-button">加载当前数据</button>
            <button @click="addPartner" class="add-button">添加合作伙伴</button>
        </div>

        <!-- 编辑表单 -->
        <div v-if="isEditing" class="edit-form">
            <h4>{{ currentPartner.name ? '编辑合作伙伴' : '添加新合作伙伴' }}</h4>
            <div class="form-group">
                <label>名称</label>
                <input v-model="currentPartner.name" type="text" placeholder="输入合作伙伴名称">
            </div>
            <div class="form-group">
                <label>Logo URL</label>
                <input v-model="currentPartner.logo" type="text" placeholder="输入Logo URL">
                <!-- Logo 预览 -->
                <div v-if="currentPartner.logo" class="logo-preview">
                    <img :src="currentPartner.logo" alt="Logo预览">
                </div>
            </div>
            <div class="form-group">
                <label>链接</label>
                <input v-model="currentPartner.link" type="text" placeholder="输入合作链接">
            </div>
            <div class="form-group">
                <label>ComingTime</label>
                <input v-model="currentPartner.ComingTime" type="text" placeholder="输入ComingTime (留空或输入'true')">
                <small class="form-note">留空表示正常显示，输入'true'表示即将到来</small>
            </div>
            <div class="items-section">
                <h5>TAG 设置</h5>
                <div class="form-group">
                    <label>Tag Top</label>
                    <input v-model="currentPartner.tagTop" type="text" placeholder="输入Tag Top">
                </div>
                <div class="form-group">
                    <label>Tag Down</label>
                    <input v-model="currentPartner.tagDown" type="text" placeholder="输入Tag Down">
                </div>
                <div class="form-group">
                    <label>Tag Color</label>
                    <div class="color-picker-container">
                        <input type="color" v-model="currentPartner.tagColor" class="color-picker">
                        <input type="text" v-model="currentPartner.tagColor" class="color-input" placeholder="#00a4f0">
                    </div>
                </div>
            </div>
            <div class="items-section">
                <h5>项目列表</h5>
                <div v-for="(item, index) in currentPartner.items" :key="index" class="item-form">
                    <div class="form-group">
                        <label>标签</label>
                        <input v-model="item.label" type="text" placeholder="输入标签">
                    </div>
                    <div class="form-group">
                        <label>数值</label>
                        <input v-model="item.value" type="text" placeholder="输入数值">
                    </div>
                </div>
            </div>
            <div class="form-actions">
                <button @click="saveEdit" class="save-button">保存</button>
                <button @click="cancelEdit" class="cancel-button">取消</button>
            </div>
        </div>

        <!-- 合作伙伴列表 -->
        <div v-if="partners.length > 0" class="partners-list">
            <h4>当前合作伙伴数据</h4>
            <div v-for="(partner, index) in partners" :key="partner.name" class="partner-item">
                <div class="partner-header">
                    <img :src="partner.logo" :alt="partner.name" class="partner-logo">
                    <div class="partner-info">
                        <h5>{{ partner.name }}</h5>
                        <a :href="partner.link" target="_blank" class="partner-link">{{ partner.link }}</a>
                    </div>
                    <div class="actions">
                        <button @click="moveUp(index)" :disabled="index === 0" class="move-button">↑</button>
                        <button @click="moveDown(index)" :disabled="index === partners.length - 1"
                            class="move-button">↓</button>
                        <button @click="editPartner(partner)" class="edit-button">编辑</button>
                        <button @click="deletePartner(index)" class="delete-button">删除</button>
                    </div>
                </div>
                <div class="partner-items">
                    <div v-for="item in partner.items" :key="item.label" class="item-info">
                        <span class="item-label">{{ item.label }}:</span>
                        <span class="item-value">{{ item.value }}</span>
                    </div>
                </div>
                <div v-if="partner.ComingTime" class="coming-time-info">
                    <span class="coming-time-badge">即将到来</span>
                </div>
                <div v-if="partner.tagTop || partner.tagDown" class="tag-info">
                    <div v-if="partner.tagTop" class="tag-item">
                        <span class="tag-label">Tag Top:</span>
                        <span class="tag-value" :style="{ color: partner.tagColor }">{{ partner.tagTop }}</span>
                    </div>
                    <div v-if="partner.tagDown" class="tag-item">
                        <span class="tag-label">Tag Down:</span>
                        <span class="tag-value" :style="{ color: partner.tagColor }">{{ partner.tagDown }}</span>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, reactive, inject, onMounted } from 'vue'

// 获取 API 基础 URL
const apiBaseUrl = inject('apiBaseUrl')

// 状态管理
const isLoading = ref(false)
const message = ref('')
const errorMessage = ref('')
const isEditing = ref(false)
const currentPartner = ref(null)
const partners = ref([])

// 表单数据
const form = reactive({
    file: null
})

// 处理文件选择
const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file && file.type === 'application/json') {
        form.file = file
        errorMessage.value = ''
    } else {
        form.file = null
        errorMessage.value = '请选择有效的 JSON 文件'
    }
}

// 格式化文件大小
const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
    else return (bytes / 1048576).toFixed(1) + ' MB'
}

// 上传文件
const uploadFile = async () => {
    if (!form.file) {
        errorMessage.value = '请选择文件'
        return
    }

    isLoading.value = true
    message.value = '上传中...'
    errorMessage.value = ''

    const formData = new FormData()
    formData.append('file', form.file)
    formData.append('filename', 'partners.json') // 固定文件名为 partners.json

    try {
        const response = await fetch(`${apiBaseUrl}/API/uploadJson.php`, {
            method: 'POST',
            body: formData
        })

        const data = await response.json()

        if (data.success) {
            message.value = '上传成功！合作伙伴数据已更新'
            form.file = null

            // 重置文件输入
            const fileInput = document.getElementById('json-file')
            if (fileInput) fileInput.value = ''
        } else {
            errorMessage.value = data.message || '上传失败'
            message.value = ''
        }
    } catch (error) {
        errorMessage.value = '上传过程中发生错误'
        console.error('上传错误:', error)
    } finally {
        isLoading.value = false
    }
}

// 获取合作伙伴数据
const fetchPartners = async () => {
    isLoading.value = true
    try {
        const response = await fetch(`${apiBaseUrl}/API/readJson.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({ jsonUrl: 'partners.json' })
        })
        const data = await response.json()
        if (!response.ok) {
            throw new Error(data?.message || `HTTP ${response.status}`)
        }
        partners.value = data.partners || []
    } catch (error) {
        errorMessage.value = '获取合作伙伴数据失败'
        console.error('获取数据错误:', error)
    } finally {
        isLoading.value = false
    }
}

// 保存合作伙伴数据
const savePartners = async () => {
    isLoading.value = true
    try {
        const response = await fetch(`${apiBaseUrl}/API/saveJson.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                filename: `partners.json`,
                // filename: 'partners.json',
                data: { partners: partners.value }
            })
        })
        const data = await response.json()
        if (data.success) {
            message.value = '保存成功！'
            setTimeout(() => {
                message.value = ''
            }, 2000)
        } else {
            errorMessage.value = data.message || '保存失败'
        }
    } catch (error) {
        errorMessage.value = '保存过程中发生错误'
        console.error('保存错误:', error)
    } finally {
        isLoading.value = false
    }
}

// 添加新合作伙伴
const addPartner = () => {
    const newPartner = {
        name: '',
        logo: '',
        link: '',
        ComingTime: '',
        tagTop: '',
        tagDown: '',
        tagColor: '#00a4f0',
        items: [
            { label: 'Free Credit', value: '' },
            { label: 'Welcome Bonus', value: '' },
            { label: 'Weekly Bonus', value: '' }
        ]
    }
    currentPartner.value = { ...newPartner }
    isEditing.value = true
}

// 编辑合作伙伴
const editPartner = (partner) => {
    currentPartner.value = JSON.parse(JSON.stringify(partner))
    isEditing.value = true
}

// 删除合作伙伴
const deletePartner = (index) => {
    if (confirm('确定要删除这个合作伙伴吗？')) {
        partners.value.splice(index, 1)
        savePartners()
    }
}

// 保存编辑
const saveEdit = () => {
    if (!currentPartner.value.name) {
        errorMessage.value = '请输入合作伙伴名称'
        return
    }

    const index = partners.value.findIndex(p => p.name === currentPartner.value.name)
    if (index !== -1) {
        // 更新现有合作伙伴
        partners.value[index] = JSON.parse(JSON.stringify(currentPartner.value))
    } else {
        // 添加新合作伙伴
        partners.value.unshift(JSON.parse(JSON.stringify(currentPartner.value)))
    }
    isEditing.value = false
    currentPartner.value = null
    savePartners()
}

// 取消编辑
const cancelEdit = () => {
    isEditing.value = false
    currentPartner.value = null
}

// 移动合作伙伴位置
const moveUp = (index) => {
    if (index > 0) {
        const temp = partners.value[index];
        partners.value[index] = partners.value[index - 1];
        partners.value[index - 1] = temp;
        savePartners();
    }
};

const moveDown = (index) => {
    if (index < partners.value.length - 1) {
        const temp = partners.value[index];
        partners.value[index] = partners.value[index + 1];
        partners.value[index + 1] = temp;
        savePartners();
    }
};

// 加载当前数据
const loadCurrentData = () => {
    fetchPartners()
}

// 检查状态 - 为了兼容父组件的调用
const checkStatus = () => {
    fetchPartners()
}

// 组件挂载时加载数据
onMounted(() => {
    fetchPartners()
})

// 暴露方法给父组件
defineExpose({
    checkStatus
})
</script>

<style scoped>
.module-container {
    background-color: #fff;
    border-radius: 5px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    padding: 20px;
}

.upload-section {
    margin-bottom: 30px;
    padding: 20px;
    background-color: #f9f9f9;
    border-radius: 5px;
    border: 1px solid #eee;
}

.upload-section h4 {
    margin-top: 0;
    margin-bottom: 15px;
    color: #333;
}

.upload-description {
    margin-bottom: 20px;
    color: #666;
    line-height: 1.5;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: bold;
    color: #333;
}

.file-preview {
    margin: 15px 0;
    padding: 12px;
    background-color: #f0f0f0;
    border-radius: 4px;
    border-left: 4px solid #1976d2;
}

.file-info {
    display: flex;
    align-items: center;
}

.file-icon {
    font-size: 24px;
    margin-right: 10px;
    color: #1976d2;
}

.file-name {
    font-size: 16px;
    color: #333;
    font-weight: 500;
    margin-right: 10px;
}

.file-size {
    font-size: 14px;
    color: #666;
}

.button-group {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
}

.primary-button {
    padding: 10px 20px;
    background-color: #FFD700;
    color: #333;
    border: none;
    border-radius: 4px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s;
}

.primary-button:hover {
    background-color: #f0c800;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.primary-button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

.upload-guide {
    background-color: #f9f9f9;
    border-radius: 5px;
    padding: 20px;
    border: 1px solid #eee;
}

.upload-guide h4 {
    margin-top: 0;
    margin-bottom: 15px;
    color: #333;
}

.upload-guide ul {
    padding-left: 20px;
    margin-bottom: 20px;
}

.upload-guide li {
    margin-bottom: 8px;
    color: #555;
    line-height: 1.5;
}

.json-format-example {
    margin-top: 20px;
}

.json-format-example h5 {
    margin-top: 0;
    margin-bottom: 10px;
    color: #333;
}

.json-preview {
    background-color: #f0f0f0;
    padding: 15px;
    border-radius: 4px;
    font-family: monospace;
    white-space: pre-wrap;
    word-break: break-all;
    font-size: 14px;
    color: #333;
    border-left: 4px solid #1976d2;
}

.success-message {
    background-color: rgba(0, 128, 0, 0.1);
    color: #2e7d32;
    padding: 10px 15px;
    border-radius: 5px;
    margin-bottom: 15px;
    border-left: 4px solid #2e7d32;
}

.error-message {
    background-color: rgba(255, 0, 0, 0.1);
    color: #d32f2f;
    padding: 10px 15px;
    border-radius: 5px;
    margin-bottom: 15px;
    border-left: 4px solid #d32f2f;
}

/* 操作按钮样式 */
.action-buttons {
    display: flex;
    gap: 1rem;
    margin-bottom: 2rem;
}

.load-button,
.add-button {
    padding: 0.8rem 1.5rem;
    border: none;
    border-radius: 5px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
}

.load-button {
    background-color: #4CAF50;
    color: white;
}

.add-button {
    background-color: #2196F3;
    color: white;
}

.load-button:hover,
.add-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

/* 编辑表单样式 */
.edit-form {
    background-color: rgba(0, 0, 0, 0.719);
    padding: 2rem;
    border-radius: 10px;
    margin-bottom: 2rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.edit-form h4 {
    color: #ffd700;
    margin-bottom: 1.5rem;
    text-align: center;
}

.edit-form .form-group {
    margin-bottom: 1.5rem;
}

.edit-form label {
    display: block;
    margin-bottom: 0.5rem;
    color: #e0e0e0;
}

.edit-form input {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 5px;
    background-color: rgba(0, 0, 0, 0.3);
    color: white;
    font-size: 1rem;
}

.items-section {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.items-section h5 {
    color: #ffd700;
    margin-bottom: 1rem;
}

.item-form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    padding: 1rem;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 5px;
}

.form-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 2rem;
}

.save-button {
    background-color: #ffd700;
    color: black;
}

.cancel-button {
    background-color: #9e9e9e;
    color: white;
}

.save-button:hover,
.cancel-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

/* 合作伙伴列表样式 */
.partners-list {
    margin-bottom: 2rem;
}

.partners-list h4 {
    color: #333;
    margin-bottom: 1rem;
}

.partner-item {
    background-color: rgba(0, 0, 0, 0.3);
    padding: 1.5rem;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    margin-bottom: 1rem;
}

.partner-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
}

.partner-logo {
    width: 80px;
    height: 80px;
    object-fit: contain;
    border-radius: 5px;
    background-color: rgba(0, 0, 0, 0.2);
    padding: 0.5rem;
}

.partner-info {
    flex: 1;
}

.partner-info h5 {
    margin: 0 0 0.5rem 0;
    color: #ffd700;
}

.partner-link {
    color: #90caf9;
    text-decoration: none;
    font-size: 0.9rem;
}

.partner-link:hover {
    text-decoration: underline;
}

.actions {
    display: flex;
    gap: 0.5rem;
}

.edit-button {
    background-color: #2196F3;
    color: white;
}

.delete-button {
    background-color: #f44336;
    color: white;
}

.move-button {
    padding: 0.5rem 0.8rem;
    background-color: #607d8b;
    color: white;
    font-weight: bold;
    border-radius: 5px;
    border: none;
    margin-right: 4px;
    cursor: pointer;
    transition: background 0.2s;
}

.move-button:disabled {
    background-color: #9e9e9e;
    cursor: not-allowed;
}

.partner-items {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.item-info {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

.item-label {
    color: #e0e0e0;
}

.item-value {
    color: #ffd700;
    font-weight: bold;
}

/* 颜色选择器样式 */
.color-picker-container {
    display: flex;
    gap: 10px;
    align-items: center;
}

.color-picker {
    width: 50px !important;
    height: 40px;
    padding: 0;
    border: none;
    border-radius: 5px;
    cursor: pointer;
}

.color-input {
    flex: 1;
    padding: 0.8rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 5px;
    background-color: rgba(0, 0, 0, 0.3);
    color: white;
    font-size: 1rem;
}

/* Logo 预览样式 */
.logo-preview {
    margin-top: 10px;
    padding: 10px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 5px;
    text-align: center;
    background-color: rgba(0, 0, 0, 0.2);
}

.logo-preview img {
    max-width: 200px;
    max-height: 100px;
    object-fit: contain;
}

/* 表单备注样式 */
.form-note {
    display: block;
    margin-top: 5px;
    color: #999;
    font-size: 12px;
    font-style: italic;
}

/* ComingTime 信息样式 */
.coming-time-info {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.coming-time-badge {
    display: inline-block;
    padding: 4px 12px;
    background-color: #ff9800;
    color: white;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
}

/* Tag 信息样式 */
.tag-info {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.tag-item {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    margin-bottom: 0.5rem;
}

.tag-label {
    color: #e0e0e0;
    font-size: 0.9rem;
}

.tag-value {
    font-weight: bold;
    font-size: 0.9rem;
}

@media (max-width: 768px) {

    .upload-section,
    .upload-guide {
        padding: 15px;
    }

    .json-preview {
        font-size: 12px;
        padding: 10px;
    }

    .action-buttons {
        flex-direction: column;
    }

    .partner-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .actions {
        width: 100%;
        justify-content: flex-end;
    }

    .item-form {
        grid-template-columns: 1fr;
    }

    .partner-items {
        grid-template-columns: 1fr;
    }
}
</style>
